import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Box, MenuItem, Select, Typography, useTheme } from '@mui/material';

import { sortBarsWithTotals } from 'src/components/followUp/common/BaseWidgets/helper';
import StackedBarWithLabel from 'src/components/followUp/common/BaseWidgets/SortableHorizontalStackedBarChart/StackedBarWithLabel';

type Bar = {
  label: string;
  stacks: Array<{ stackLabel: string; value: number }>;
  comparison?: {
    stacks: Array<{ stackLabel: string; value: number }>;
  };
};

interface SortableHorizontalStackedBarChartProps {
  stackLabels: Array<{
    name: string;
    color: string;
    borderColor: string;
    comparisonColor?: string;
  }>;
  bars: Bar[];
}

const SortableHorizontalStackedBarChart: FC<
  SortableHorizontalStackedBarChartProps
> = ({ stackLabels, bars }) => {
  const {
    colors: { neutral },
  } = useTheme();

  const barsWithTotals = useMemo(
    () =>
      bars.map((bar) => {
        const calcTotal = (acc: number, stack: { value: number }) =>
          stack.value + acc;
        return {
          ...bar,
          total: bar.stacks.reduce(calcTotal, 0),
          comparison: bar.comparison
            ? {
                ...bar.comparison,
                total: bar.comparison.stacks.reduce(calcTotal, 0),
              }
            : undefined,
        };
      }),
    [bars],
  );

  const [sortBy, setSortBy] = useState('frequency');
  const sortedBars = useMemo(
    () => sortBarsWithTotals(barsWithTotals, sortBy),
    [barsWithTotals, sortBy],
  );

  const highestTotal = useMemo(
    () => Math.max(...barsWithTotals.map(({ total }) => total)),
    [barsWithTotals],
  );

  return (
    <Box
      width="100%"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: '100%',
        padding: 3,
        paddingTop: 0,
      }}
    >
      <Box sx={{ borderBottom: '1px solid #F3F4F6', paddingY: 1.5 }}>
        <Select
          value={sortBy}
          onChange={(event) => setSortBy(event.target.value)}
          size="small"
          disableUnderline
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            backgroundColor: theme.colors.neutral[50],
            color: theme.colors.neutral[500],
            width: '20rem',
            borderRadius: 1,
          })}
          variant="standard"
          SelectDisplayProps={{
            style: { padding: '8px 14px', borderRadius: 4, fontWeight: 600 },
          }}
        >
          <MenuItem value="frequency">
            <Typography variant="body3" fontWeight={600} color={neutral[600]}>
              Sorted by Frequency
            </Typography>
          </MenuItem>
          {stackLabels.map((stackLabel) => (
            <MenuItem key={stackLabel.name} value={stackLabel.name}>
              <Typography variant="body3" fontWeight={600} color={neutral[600]}>
                Sorted by {stackLabel.name}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          height: '100%',
          overflow: 'scroll',
        }}
      >
        {sortedBars.map((bar) => (
          <Box key={bar.label} display="flex" flexDirection="column" gap="4px">
            <StackedBarWithLabel
              stackLabels={stackLabels}
              label={bar.label}
              total={bar.total}
              stacks={bar.stacks}
              highestTotal={highestTotal}
              height="2.625rem"
            />
            {bar.comparison && (
              <StackedBarWithLabel
                stackLabels={stackLabels}
                total={bar.comparison.total}
                stacks={bar.comparison.stacks}
                highestTotal={highestTotal}
                height="1.5rem"
                comparison
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SortableHorizontalStackedBarChart;
